import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import SetpointsTable from "./SetpointsTable";
import SetpointsResourceChart from "./SetpointsResourceChart";
import MemberEnergyChart from "./MemberEnergyChart";
import BaselineChart from "./BaselineChart";
import OffersTable from "./OffersTable";
import OpportunityCostChart from "./OpportunityCostChart";
import ScenarioChart from "./ScenarioChart";
import PromoterRetailChart from "./PromoterRetailChart";
import PromoterLiquidBatChart from "./PromoterLiquidBatChart";
import PromoterBatSocChart from "./PromoterBatSocChart";
import PromoterCostChart from "./PromoterCostChart";
import PromoterSolarAllocChart from "./PromoterSolarAllocChart";

/**
 * Generic capability-driven module view.
 *
 * Tab layouts (driven by capabilities):
 *   setpoints + offers + anyChart  → [Offers | Setpoints | Charts]  tabValue 0/1/2
 *   setpoints + offers             → [Offers | Setpoints]            tabValue 0/1
 *   setpoints + anyChart           → [Setpoints | Charts]            tabValue 0/1
 *   setpoints only                 → no tabs, SetpointsTable
 *   offers only                    → no tabs, OffersTable
 *
 * Charts tab layout:
 *   2+ charts → responsive grid (2 columns, last item full-width if odd count)
 *   1 chart   → full width
 */
function ModuleView({ moduleType, capabilities, tabValue, startDatetime, endDatetime }) {
  const { t } = useTranslation(["community"]);

  const hasSetpoints =
    capabilities.includes("setpoint_calculation") ||
    capabilities.includes("local_optimization") ||
    capabilities.includes("external_setpoint_reception");

  const hasOffers =
    capabilities.includes("offer_generation") || capabilities.includes("external_offer_reception");

  const hasSetpointChart = capabilities.includes("setpoint_chart");
  const hasMemberEnergyChart = capabilities.includes("member_energy_chart");
  const hasBaselineChart = capabilities.includes("baseline_chart");
  const hasOpportunityCostChart = capabilities.includes("opportunity_cost_chart");
  const hasScenarioChart = capabilities.includes("scenario_chart");
  const hasRetailTransactionsChart = capabilities.includes("retail_transactions_chart");
  const hasLiquidBatChart = capabilities.includes("liquid_bat_chart");
  const hasBatSocChart = capabilities.includes("bat_soc_chart");
  const hasCostComparisonChart = capabilities.includes("cost_comparison_chart");
  const hasSolarAllocChart = capabilities.includes("solar_allocation_chart");
  const hasAnyChart =
    hasSetpointChart ||
    hasMemberEnergyChart ||
    hasBaselineChart ||
    hasOpportunityCostChart ||
    hasScenarioChart ||
    hasRetailTransactionsChart ||
    hasLiquidBatChart ||
    hasBatSocChart ||
    hasCostComparisonChart ||
    hasSolarAllocChart;

  // Track which charts have no data: map of chartKey → title translation key
  const [emptyCharts, setEmptyCharts] = useState({});

  const makeDataHandler = useCallback(
    (chartKey, titleKey) => (hasData) => {
      setEmptyCharts((prev) => {
        const wasEmpty = Object.prototype.hasOwnProperty.call(prev, chartKey);
        if (hasData) {
          if (!wasEmpty) return prev;
          const next = { ...prev };
          delete next[chartKey];
          return next;
        }
        if (wasEmpty) return prev;
        return { ...prev, [chartKey]: titleKey };
      });
    },
    []
  );

  const chartProps = { moduleType, startDatetime, endDatetime };

  const activeCharts = [
    hasSetpointChart && (
      <SetpointsResourceChart
        key="setpoints"
        {...chartProps}
        onDataStatus={makeDataHandler("setpoints", "unified_ems.chart_title")}
      />
    ),
    hasMemberEnergyChart && (
      <MemberEnergyChart
        key="member"
        {...chartProps}
        onDataStatus={makeDataHandler("member", "unified_ems.member_energy_chart_title")}
      />
    ),
    hasBaselineChart && (
      <BaselineChart
        key="baseline"
        {...chartProps}
        onDataStatus={makeDataHandler("baseline", "unified_ems.baseline_chart_title")}
      />
    ),
    hasOpportunityCostChart && (
      <OpportunityCostChart
        key="opp"
        {...chartProps}
        onDataStatus={makeDataHandler("opp", "unified_ems.opportunity_cost_chart_title")}
      />
    ),
    hasScenarioChart && (
      <ScenarioChart
        key="scenario"
        {...chartProps}
        onDataStatus={makeDataHandler("scenario", "unified_ems.scenario_chart_title")}
      />
    ),
    hasBatSocChart && (
      <PromoterBatSocChart
        key="promoter_bat_soc"
        {...chartProps}
        onDataStatus={makeDataHandler(
          "promoter_bat_soc",
          "unified_ems.promoter_bat_soc_chart_title"
        )}
      />
    ),
    hasLiquidBatChart && (
      <PromoterLiquidBatChart
        key="promoter_liquid_bat"
        {...chartProps}
        onDataStatus={makeDataHandler(
          "promoter_liquid_bat",
          "unified_ems.promoter_liquid_bat_chart_title"
        )}
      />
    ),
    hasSolarAllocChart && (
      <PromoterSolarAllocChart
        key="promoter_solar_alloc"
        {...chartProps}
        onDataStatus={makeDataHandler(
          "promoter_solar_alloc",
          "unified_ems.promoter_solar_alloc_chart_title"
        )}
      />
    ),
    hasRetailTransactionsChart && (
      <PromoterRetailChart
        key="promoter_retail"
        {...chartProps}
        onDataStatus={makeDataHandler("promoter_retail", "unified_ems.promoter_retail_chart_title")}
      />
    ),
    hasCostComparisonChart && (
      <PromoterCostChart
        key="promoter_cost"
        {...chartProps}
        onDataStatus={makeDataHandler("promoter_cost", "unified_ems.promoter_cost_chart_title")}
      />
    ),
  ].filter(Boolean);

  const omittedTitles = Object.values(emptyCharts);

  const omissionNote =
    omittedTitles.length > 0 ? (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight={500} mb={0.5} color="text.secondary">
          {t("unified_ems.charts_omitted_header")}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {omittedTitles.map((titleKey) => (
            <li key={titleKey}>
              <Typography variant="body2" color="text.secondary">
                {t(titleKey)}
              </Typography>
            </li>
          ))}
        </Box>
      </Box>
    ) : null;

  // Split into visible (data confirmed or unknown) vs confirmed-empty charts.
  // Empty charts are kept mounted (display:none) so they can still report status.
  const visibleChartElements = activeCharts.filter(
    (c) => !Object.prototype.hasOwnProperty.call(emptyCharts, c.key)
  );
  const hiddenChartElements = activeCharts.filter((c) =>
    Object.prototype.hasOwnProperty.call(emptyCharts, c.key)
  );

  const chartsTab = (
    <>
      {hiddenChartElements.map((chart) => (
        <Box key={chart.key} sx={{ display: "none" }}>
          {chart}
        </Box>
      ))}
      {/* One chart per row, full width — these are detailed time-series that
          need the horizontal space to be readable. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>{visibleChartElements}</Box>
      {omissionNote}
    </>
  );

  // setpoints + offers → tabs: 0=Offers, 1=Setpoints, 2=Charts (if hasAnyChart)
  if (hasSetpoints && hasOffers) {
    return (
      <>
        {tabValue === 0 && (
          <OffersTable
            moduleType={moduleType}
            startDatetime={startDatetime}
            endDatetime={endDatetime}
          />
        )}
        {tabValue === 1 && (
          <SetpointsTable
            moduleType={moduleType}
            startDatetime={startDatetime}
            endDatetime={endDatetime}
          />
        )}
        {tabValue === 2 && hasAnyChart && chartsTab}
      </>
    );
  }

  // setpoints + chart only → tabs: 0=Setpoints, 1=Charts
  if (hasSetpoints && hasAnyChart) {
    return (
      <>
        {tabValue === 0 && (
          <SetpointsTable
            moduleType={moduleType}
            startDatetime={startDatetime}
            endDatetime={endDatetime}
          />
        )}
        {tabValue === 1 && chartsTab}
      </>
    );
  }

  // Fallback: no tabs
  return (
    <>
      {hasSetpoints && (
        <SetpointsTable
          moduleType={moduleType}
          startDatetime={startDatetime}
          endDatetime={endDatetime}
        />
      )}
      {hasOffers && (
        <OffersTable
          moduleType={moduleType}
          startDatetime={startDatetime}
          endDatetime={endDatetime}
        />
      )}
      {!hasSetpoints && !hasOffers && <SetpointsTable moduleType={moduleType} />}
    </>
  );
}

ModuleView.defaultProps = {
  capabilities: [],
  tabValue: 0,
  startDatetime: undefined,
  endDatetime: undefined,
};

ModuleView.propTypes = {
  moduleType: PropTypes.string.isRequired,
  capabilities: PropTypes.arrayOf(PropTypes.string),
  tabValue: PropTypes.number,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
};

export default ModuleView;
